import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Pill, PillDot } from '../../../ui';
import {
    ActionsLigne, CelluleNom, LienIcone, MonoCell, Muted,
} from '../../../security/security.styled';
import { formatDate } from '../../../security/severity';
import { PaneTable, PillOuiNon, triAlpha } from '../paneTable';

// Les neuf valeurs constatées d'enrollmentType, en clair. « AzureDomainJoined »
// est de loin la première (1 415 appareils) et c'est le critère retenu pour
// l'historique de conformité.
const LIBELLES_RATTACHEMENT = {
    AzureDomainJoined: 'Joint au domaine',
    AutoEnrollment: 'Enrôlement automatique',
    UserEnrollment: 'Enrôlement utilisateur',
    AndroidEnterpriseFullyManaged: 'Android entièrement géré',
    AndroidEnterpriseCorporateWorkProfile: 'Profil professionnel Android',
    AppleACMEBasicBYOD: 'Apple BYOD',
    AppleUserEnrollment: 'Enrôlement Apple',
    Unknown: 'Inconnu',
};

function dateHeure(valeur) {
    if (!valeur) return '—';
    const date = new Date(valeur);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
}

// Deux destinations pour un même appareil, posées sur son nom plutôt que dans
// une colonne : la fiche interne pour la conformité détaillée, les
// vulnérabilités et l'inventaire applicatif, le portail Microsoft pour agir sur
// l'objet Entra.
function CelluleAppareil({ appareil }) {
    // La fiche interne résout un GUID Entra, un identifiant Intune, un
    // identifiant Defender ou un nom. `deviceIdAAD` est renseigné sur les 400
    // appareils rattachés à un utilisateur qui ont été contrôlés, tous au format
    // GUID attendu ; le nom reste le repli.
    const cleInterne = appareil.deviceIdAAD || appareil.displayName;
    const objetEntra = appareil.objectId || appareil.id;

    return (
        <CelluleNom>
            <MonoCell className="nom">{appareil.displayName || <Muted>Sans nom</Muted>}</MonoCell>
            <ActionsLigne>
                {cleInterne && (
                    <LienIcone
                        $mini
                        as={Link}
                        to={`/Security/Devices?device=${encodeURIComponent(cleInterne)}`}
                        title={`Fiche Habilitations de ${appareil.displayName}`}
                        aria-label="Ouvrir la fiche dans Habilitations"
                    >
                        <i className="bx bx-shield-quarter" />
                    </LienIcone>
                )}
                {objetEntra && (
                    <LienIcone
                        $mini
                        href={`https://portal.azure.com/#view/Microsoft_AAD_Devices/DeviceDetailsMenuBlade/~/Properties/objectId/${objetEntra}/deviceId/`}
                        target="_blank"
                        rel="noreferrer"
                        title={`Objet Entra ID de ${appareil.displayName}`}
                        aria-label="Ouvrir dans Entra ID"
                    >
                        <i className="bx bxl-microsoft" />
                    </LienIcone>
                )}
            </ActionsLigne>
        </CelluleNom>
    );
}

const TRI_PAR_NOM = [{ id: 'displayName', desc: false }];

const COLONNES = [
    {
        header: 'Appareil',
        accessorKey: 'displayName',
        sortingFn: triAlpha,
        cell: ({ row }) => <CelluleAppareil appareil={row.original} />,
    },
    {
        header: 'Système',
        accessorFn: (row) => [row.operatingSystem, row.operatingSystemVersion].filter(Boolean).join(' '),
        cell: ({ getValue }) => (getValue() ? <Muted>{getValue()}</Muted> : <Muted>—</Muted>),
    },
    {
        header: 'Rattachement',
        accessorKey: 'enrollmentType',
        cell: ({ getValue }) => (getValue()
            ? <Pill $tone={getValue() === 'AzureDomainJoined' ? 'accent' : 'neutral'}>
                {LIBELLES_RATTACHEMENT[getValue()] || getValue()}
            </Pill>
            : <Muted>—</Muted>),
    },
    {
        header: 'Gestion',
        accessorKey: 'isManaged',
        cell: ({ getValue }) => <PillOuiNon valeur={getValue()} oui="Géré" non="Non géré" tonNon="neutral" />,
    },
    {
        header: 'Conformité',
        accessorKey: 'isCompliant',
        // `isCompliant` est un BOOLÉEN depuis la conversion faite à la collecte.
        // Les deux formes sont acceptées, d'anciens documents pouvant encore
        // porter la chaîne.
        cell: ({ getValue }) => <PillOuiNon valeur={getValue()} oui="Conforme" non="Non conforme" />,
    },
    {
        header: 'Créé le',
        accessorKey: 'createdDateTime',
        cell: ({ getValue }) => <Muted>{formatDate(getValue())}</Muted>,
    },
    {
        header: 'Dernière connexion',
        accessorKey: 'approximateLastSignInDateTime',
        cell: ({ getValue }) => <Muted>{dateHeure(getValue())}</Muted>,
    },
];

export function UserDevices() {
    const { isActiveDevice, currentDevice } = useSelector((state) => state.device);

    const donnees = isActiveDevice ? (currentDevice?.value ?? currentDevice) : [];
    const appareils = Array.isArray(donnees) ? donnees : [];

    const nonConformes = appareils.filter((a) => a.isCompliant === false || a.isCompliant === 'false').length;

    return (
        <PaneTable
            titre="Appareils"
            colonnes={COLONNES}
            donnees={appareils}
            triInitial={TRI_PAR_NOM}
            placeholder="Nom, système…"
            badges={nonConformes > 0 && (
                <Pill $tone="danger">
                    <PillDot />
                    {nonConformes} non conforme{nonConformes > 1 ? 's' : ''}
                </Pill>
            )}
            vide={{
                icone: 'bx-laptop',
                titre: 'Aucun appareil rattaché',
                texte: "Aucun appareil Entra ID n'est enregistré au nom de ce compte.",
            }}
        />
    );
}
